import _ from 'lodash'
import app from '../app.jsx'

class ActionHandler {
  constructor (handlerApi) {
    this._queue = []
    this._bInProgress = false
    this._handlerApi = handlerApi
    this._dataCallback = null
    this._initialData = null
    this._requestIdx = 0
  }

  setInitialData (data) {
    this._initialData = data || {}
  }

  setDataCallback (callback) {
    this._dataCallback = callback
  }

  call (servicePath, method, id, data, params, callback) {
    // Set temporary id to avoid duplicate creation
    // If one create request is pending, Same kind of data again sent
    // 1. Merge the create requests if previous step is InProgress ( OR )
    // 2. Convert create request into patch request, if it is created successfully
    const requestQueueId = this._requestIdx++
    if (method === 'create' && !data.tempId) {
      data.tempId = requestQueueId
    }

    this._queue.push({
      index: requestQueueId,
      success: false,
      servicePath,
      method,
      id,
      data,
      params,
      result: null,
      error: null,
      callback,
      noTries: 0
    })

    // If no service is running, trigger the process
    if (!this._bInProgress) {
      this._processNext()
    }

    return requestQueueId
  }

  /* Private functions */
  _processNext () {
    // If queue is empty stop processing
    if (this._queue.length <= 0) {
      this._bInProgress = false
      return
    }

    // Merge all actions
    this._mergeActions()

    // Get 5 acitons from queue
    const actions = this._queue.splice(0, 5)
    if (actions.length <= 0) {
      this._bInProgress = false
      return
    }

    // Call the action handler service
    this._bInProgress = true
    this._callActionHandler(actions)
  }

  _callActionHandler (actions) {
    const handlerData = Object.assign({ actions: [] }, this._initialData || {})
    handlerData.actions = actions.map((action) => {
      return {
        success: false,
        index: action.index,
        servicePath: action.servicePath,
        method: action.method,
        id: action.id || null,
        data: action.data || null,
        params: action.params || {},
        noTries: action.noTries++,
        result: null,
        error: null
      }
    })

    let pageData = null

    app
      .service(this._handlerApi)
      .create(handlerData)
      .then((apiRes) => {
        const retryActions = []

        if (apiRes.pageData) {
          pageData = apiRes.pageData
        }

        // Verify all the results
        apiRes.actions.forEach((actionRes) => {
          const action = _.find(
            actions,
            (obj) => obj.index === actionRes.index
          )
          if (action) {
            action.success = actionRes.success
            action.result = actionRes.result
            action.error = actionRes.error

            if (action.success) {
              // If create request is successfull, Convert all pending create requests into patch if tempId is same
              if (
                action.method === 'create' &&
                action.result &&
                action.result.id
              ) {
                this._queue.forEach((pendingAction) => {
                  if (pendingAction.data.tempId === action.data.tempId) {
                    pendingAction.method = 'patch'
                    pendingAction.id = action.result.id
                  }
                })
              }

              // Trigger the callback with response
              if (action.callback) {
                action.callback(null, action.result)
              }
            } else if (action.noTries < 1) {
              // Retry action if noRetry is 2
              if (action) {
                retryActions.push(action)
              }
            } else {
              // Trigger the callback with error
              if (action.callback) {
                action.callback(action.error)
              }
            }
          }
        })

        // If all request success process the next batch
        if (retryActions.length === 0) {
          this._processNext()

          // Trigger the callback with reload data of the page
          if (pageData && this._dataCallback) {
            this._dataCallback(pageData)
          }
        } else {
          // Retry the failed actions again
          this._callActionHandler(retryActions)
        }
      })
      .catch((err) => {
        app.error('ActionHandler::_callActionHandler - Error ', err)
        this._processNext()
      })
  }

  _mergeActions () {
    // TODO: Make sure that we are merging will not cause any issues, due to change in sequence of calls
    const removeIds = []
    this._queue.forEach((action) => {
      // Skip if the actions is already merged
      if (!removeIds.includes(action.index)) {
        this._queue.forEach((upcomingAction) => {
          // If same action, ignore it
          // Only merge update|patch requests
          if (
            action.index !== upcomingAction.index &&
            action.servicePath === upcomingAction.servicePath &&
            action.method === upcomingAction.method
          ) {
            let bMerge = false
            if (
              action.method === 'create' &&
              action.data.tempId === upcomingAction.data.tempId
            ) {
              bMerge = true
            } else if (
              ['update', 'patch'].includes(action.method) &&
              action.id === upcomingAction.id
            ) {
              bMerge = true
            }

            if (bMerge) {
              // Merge data of lastest action to oldest action in queue
              Object.keys(upcomingAction.data).forEach((key) => {
                action.data[key] = upcomingAction.data[key]
              })

              // Remove upcoming action, since it is already merged
              removeIds.push(upcomingAction.index)
            }
          }
        })
      }
    })

    // Remove merged calls
    _.remove(this._queue, (obj) => removeIds.includes(obj.index))
  }
}

export default ActionHandler
